import React, { useState, useEffect } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, useMediaQuery, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Link, useNavigate } from "react-router-dom";
import Routes from '../../Routes';
import Footer from './Footer';
import { getSidebarRoutes, isRouteEnabled, getRouteStatusInfo } from '../../utils/routeStatus';
import { getClientFilteredSidebarRoutes } from '../../utils/clientPrivilegeService';

const drawerWidth = 180;
const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        width: drawerWidth,
        backgroundColor: '#050035 !important',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between', // Align items at the start and end of the container
        // height: '90%',
    },
    listItem: {
        color: '#fff',
        opacity: 0.7,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        '&:hover': {
            opacity: 1,
            transform: 'scale(1.05)',
        },
    },
    listItemText: {
        fontSize: '10px',
    },
    activeListItem: {
        color: '#fff',
        opacity: 1,
        transform: 'scale(1.05)', // Apply the transform to active items
        transition: 'opacity 0.3s ease',
        '&:hover': {
            opacity: 1,
        },
    },
    listItemIcon: {
        color: '#fff !important',
        minWidth: '35px !important', // Reduce default minWidth from 56px to 35px
    },
    disabledListItem: {
        color: '#fff',
        opacity: 0.4,
        cursor: 'not-allowed',
        transition: 'opacity 0.3s ease',
        '&:hover': {
            opacity: 0.5,
            transform: 'none',
        },
    },
    disabledListItemIcon: {
        color: '#fff !important',
        opacity: 0.4,
        minWidth: '35px !important', // Consistent spacing for disabled items
    },
}));

const SideBar = ({ open, onClose }) => {
    const [activeMenuItem, setActiveMenuItem] = useState(1);
    const [sidebarRoutes, setSidebarRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const classes = useStyles();
    const matches = useMediaQuery("(min-width:200px)");
    const userRole = localStorage.getItem("userRole");
    const currentEnvironment = process.env.REACT_APP_STAGE; // Get current environment
    var navigate = useNavigate();

    // Get user's batch/program code for client privilege filtering
    const getUserBatchCode = () => {
        try {
            const userDataString = localStorage.getItem("userData");
            if (!userDataString) {
                console.log("[SideBar] No userData in localStorage");
                return "";
            }
            
            const userData = JSON.parse(userDataString);
            const batchCode = userData?.programCode || userData?.user_batch || "";
            
            // Ensure we return a string
            if (typeof batchCode !== 'string') {
                console.warn("[SideBar] Batch code is not a string:", batchCode);
                return String(batchCode || "");
            }
            
            return batchCode;
        } catch (error) {
            console.error("[SideBar] Error parsing userData:", error);
            return "";
        }
    };

    // Load client-filtered sidebar routes
    useEffect(() => {
        const loadFilteredRoutes = async () => {
            setLoading(true);
            try {
                const userBatchCode = getUserBatchCode();
                console.log('[SideBar] User batch code:', userBatchCode);
                
                // Get client-filtered routes
                const filteredRoutes = await getClientFilteredSidebarRoutes(
                    Routes.mainRoutes, 
                    userRole, 
                    userBatchCode, 
                    currentEnvironment
                );
                
                console.log('[SideBar] Client-filtered routes:', filteredRoutes);
                setSidebarRoutes(filteredRoutes);
            } catch (error) {
                console.error('[SideBar] Error loading client-filtered routes:', error);
                // Fallback to default sidebar routes if client privilege filtering fails
                const fallbackRoutes = getSidebarRoutes(Routes.mainRoutes, userRole, currentEnvironment);
                setSidebarRoutes(fallbackRoutes);
            }
            setLoading(false);
        };

        loadFilteredRoutes();
    }, [userRole, currentEnvironment]);

    const handleMenuItemClick = (index, path, route) => {
        // Check if route is enabled before navigation
        if (isRouteEnabled(route, currentEnvironment)) {
            setActiveMenuItem(index);
            navigate(path);
        }
    };

    return (
        <Drawer
            classes={{ paper: classes.drawerPaper }}
            variant={matches ? "permanent" : "persistent"}
            sx={{
                whiteSpace: "nowrap",
                width: open ? drawerWidth : matches ? 60 : 0,
                "& .MuiDrawer-paper": {
                    marginTop: "64px",
                    width: open ? drawerWidth : matches ? 60 : 0,
                    
                },
            }}
            open={open}
            onClose={onClose}
        >
            <List>
                {loading ? (
                    <ListItem>
                        <Box display="flex" justifyContent="center" width="100%" py={2}>
                            <CircularProgress size={24} sx={{ color: '#fff' }} />
                        </Box>
                    </ListItem>
                ) : (
                    sidebarRoutes.map((item, index) => {
                        const statusInfo = getRouteStatusInfo(item, currentEnvironment);
                        const isEnabled = statusInfo.enabled;
                        
                        const listItemClass = isEnabled 
                            ? `${classes.listItem} ${index === activeMenuItem ? classes.activeListItem : ''}` 
                            : classes.disabledListItem;
                        
                        const iconClass = isEnabled 
                            ? classes.listItemIcon 
                            : classes.disabledListItemIcon;

                        return (
                            <ListItem
                                key={item.name}
                                className={listItemClass}
                                component={isEnabled ? Link : 'div'}
                                to={isEnabled ? item.path : undefined}
                                onClick={() => handleMenuItemClick(index, item.path, item)}
                                title={!isEnabled ? `${item.name} - ${statusInfo.statusLabel}` : item.name}
                            >
                                <ListItemIcon className={iconClass}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.name}
                                    primaryTypographyProps={{ 
                                        style: { 
                                            fontSize: '12px',
                                            fontWeight: 400 
                                        } 
                                    }}
                                />
                            </ListItem>
                        );
                    })
                )}
            </List>
            <Box>
                {open && (<Footer />)}
            </Box>
        </Drawer >
    );
};

export default SideBar;